import React, { useState } from 'react'
import clsx from 'clsx'
import {
  makeStyles,
  createStyles,
  Typography,
  Dialog,
  Container,
  Grid,
  Fab,
} from '@material-ui/core'
import ImgKitHandler from '../ImgKitHandler/ImgKitHandler'
import useShopStyle from '../Shop/Shop.style'
import Glider from 'react-glider'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons'
import Close from '@material-ui/icons/Close'
import { useMatomo } from '@datapunt/matomo-tracker-react'

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      cursor: 'pointer',
    },
    foot: {
      padding: theme.spacing(1, 2),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
    },
    title: {
      fontWeight: 700,
      lineHeight: 1.2,
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '90%',
      fontWeight: 700,
      lineHeight: 1,
      padding: '.25em',
      minWidth: '1.75em',
      height: '1.75em',
      borderRadius: '1.75em',
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    modalContent: {
      padding: theme.spacing(3),
    },
    modalMedias: {},
    modalDetails: {
      [theme.breakpoints.up('sm')]: {
        display: 'flex',
        justifYContent: 'center',
        alignItems: 'center',
      },
    },
    modalDetailsContent: {
      padding: theme.spacing(1),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(3),
      },
      '& iframe': {
        width: '100%',
      },
    },
    modalClose: {
      position: 'absolute',
      zIndex: 2,
      top: theme.spacing(1),
      right: theme.spacing(1),
      cursor: 'pointer',
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
      boxShadow: theme.shadows[0],
      '&:hover, &:focus': {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
      },
    },
  })
)

type TProps = {
  content: any
  settings: {
    ratio: string
    cardWidth: number
    largeImgWidth: number
    isMobile: boolean
    currentUrl: string
  }
}

const PackProductCard = ({ content, settings }: TProps) => {
  const LayoutStyle = {}
  const shopClasses = useShopStyle(LayoutStyle)
  const classes = useStyles()
  const { trackEvent } = useMatomo()
  const [openModal, setOpenModal] = useState(false)

  const setOpenModalProduct = () => {
    setOpenModal(true)
    trackEvent({
      category: 'Packs',
      action: `Open Pack modale from ${settings.currentUrl}`,
      name: content.product?.name,
    })
  }

  const setCloseModalProduct = () => {
    setOpenModal(false)
    trackEvent({
      category: 'Packs',
      action: `Close Pack modale from ${settings.currentUrl}`,
      name: content.product?.name,
    })
  }

  return (
    <>
      <article
        className={clsx('pack-product', classes.root, shopClasses.shopCard)}
        onClick={setOpenModalProduct}
      >
        <div className={'pack-product__media'} data-ratio={settings.ratio}>
          <ImgKitHandler
            imagePaths={content.product?.images[0]}
            settings={{
              quality: 75,
              width: settings.cardWidth,
              ar: settings.ratio?.substring(3),
            }}
          />
        </div>
        <div className={clsx('pack-product__foot', classes.foot)}>
          <Typography
            className={clsx('pack-product__title', classes.title)}
            variant="body1"
            component="h2"
          >
            {content.product?.name}
          </Typography>
          {content.packProductQuantity > 1 && (
            <span className={clsx('pack-product__badge', classes.badge)}>
              x{content.packProductQuantity}
            </span>
          )}
        </div>
      </article>
      <Dialog
        open={openModal}
        fullScreen={settings.isMobile}
        onClose={() => setOpenModal(false)}
        maxWidth={'lg'}
        className={'pack-product-modal'}
      >
        <Container
          maxWidth={'lg'}
          className={clsx('pack-product-modal__content', classes.modalContent)}
        >
          <Fab
            onClick={setCloseModalProduct}
            className={clsx('pack-product-modal__close', classes.modalClose)}
            size="small"
          >
            <Close />
          </Fab>
          <div>
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                md={6}
                className={clsx(
                  'pack-product-modal__medias',
                  classes.modalMedias
                )}
              >
                {content.product?.images?.length > 1 ? (
                  <Glider
                    hasArrows
                    draggable
                    scrollLock
                    slidesToShow={1}
                    iconLeft={<KeyboardArrowLeft />}
                    iconRight={<KeyboardArrowRight />}
                  >
                    {content.product?.images?.map((image, index) => (
                      <div key={index}>
                        <div data-ratio={settings.ratio}>
                          <ImgKitHandler
                            settings={{
                              quality: 75,
                              width: settings.largeImgWidth,
                              ar: settings.ratio?.substring(3),
                            }}
                            imagePaths={image}
                          />
                        </div>
                      </div>
                    ))}
                  </Glider>
                ) : (
                  <div data-ratio={settings.ratio}>
                    <ImgKitHandler
                      settings={{
                        quality: 75,
                        width: settings.largeImgWidth,
                        ar: settings.ratio?.substring(3),
                      }}
                      imagePaths={content.product?.images[0]}
                    />
                  </div>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                className={clsx(
                  'pack-product-modal__details',
                  classes.modalDetails
                )}
              >
                <div
                  className={clsx(
                    'pack-product-modal__details-content',
                    classes.modalDetailsContent
                  )}
                >
                  <Typography
                    variant="h2"
                    component="div"
                    gutterBottom
                    className="pack-product-modal__title"
                  >
                    {content.product?.name}
                  </Typography>
                  <Typography
                    component={'div'}
                    className="pack-product-modal__desc"
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: content.product?.description,
                      }}
                    />
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </div>
        </Container>
      </Dialog>
    </>
  )
}

export default PackProductCard
